import { z } from 'zod'

/**
 * Catálogo de EVENTOS que o servidor comunica ao jogador.
 *
 * Nenhum texto para o jogador nasce no servidor. O servidor diz O QUE
 * aconteceu — um código estável e os parâmetros que o código carrega — e é o
 * cliente que escolhe as palavras, no idioma do jogador. Trocar a frase, ou o
 * idioma, deixa de exigir um deploy do servidor; e o mesmo código pode virar
 * um ícone, um som ou um log, sem ninguém ter de reconhecer uma frase.
 *
 * Este arquivo é a ÚNICA lista. Cada código aparece uma vez, com os nomes dos
 * parâmetros que viajam junto. O cliente tem um teste que exige uma tradução
 * para cada código daqui, em cada idioma — então acrescentar um código sem
 * tradução falha o teste do cliente, e não a tela do jogador.
 *
 * Os nomes de parâmetro são a documentação: `['opcao']` diz que o cliente
 * pode escrever `{opcao}` na frase e vai receber um valor.
 *
 * O `message` em texto continua nas mensagens por compatibilidade com clientes
 * antigos. Um cliente que conhece o código o prefere e ignora o texto.
 */
export const EVENTOS = {
  // ------------------------------------------------------------ autenticação
  /** O login não pôde ser verificado por um motivo interno. */
  'auth.falhou': [],
  /** Tentou operar salas sem ter assinado o login. */
  'auth.necessaria': [],
  'auth.endereco_invalido': [],
  'auth.nonce_desconhecido': [],
  'auth.nonce_expirado': [],
  'auth.nonce_de_outra_carteira': [],
  'auth.mensagem_nao_confere': [],
  'auth.assinatura_mal_formada': [],
  'auth.assinatura_invalida': [],
  'auth.sessao_desconhecida': [],
  'auth.sessao_expirada': [],

  // ------------------------------------------------------------------- geral
  'geral.interno': [],
  'geral.mensagem_invalida': [],

  // ------------------------------------------------------------------- motor
  /** WebKit (Safari e todo navegador de iPhone) ainda não passou pela bateria. */
  'motor.webkit_nao_verificado': [],
  /** Não deu para identificar o motor — e desconhecido é barrado. */
  'motor.desconhecido': [],

  // ------------------------------------------------------------------- lobby
  'lobby.entrada_fora_dos_limites': [],
  'lobby.ja_em_sala': [],
  'lobby.sem_reserva_de_mesa': [],
  /* A reserva passou do prazo antes de a confirmação chegar. */
  'lobby.reserva_vencida': [],
  'lobby.sem_reserva_de_entrada': [],
  'lobby.deposito_nao_encontrado': [],
  'lobby.deposito_de_outra_carteira': [],
  'lobby.deposito_valor_diferente': [],
  /** O prazo on-chain não comporta uma partida inteira. */
  'lobby.prazo_curto_demais': [],
  /** Modo manutenção: não se cria nem se entra em mesa ou evento até a atualização. */
  'lobby.manutencao': [],
  'lobby.sala_nao_encontrada': [],
  'lobby.sala_cheia': [],
  /** Eventos de mesa (melhor de 3, torneio). */
  'eventomesa.nao_encontrado': [],
  'eventomesa.cheio': [],
  'eventomesa.fechado': [],
  'eventomesa.ja_em_evento': [],
  'eventomesa.fora': [],
  'eventomesa.formato_invalido': [],
  'eventomesa.entrada_invalida': [],
  'eventomesa.inscricao_nao_paga': [],
  'eventomesa.pago_indisponivel': [],
  'eventomesa.id_em_uso': [],
  'eventomesa.nao_e_o_dono': [],
  'eventomesa.ocupado_em_mesa': [],
  'eventomesa.sair_depois_de_comecar': [],
  'lobby.propria_mesa': [],
  'lobby.prazo_vencido': [],
  'lobby.partida_nao_encontrada_na_chain': [],
  'lobby.deposito_do_oponente_pendente': [],
  'lobby.entrou_outra_carteira': [],
  'lobby.so_criador_cancela': [],
  'lobby.mesa_ja_comprometida': [],
  'lobby.partida_ainda_na_chain': [],
  'lobby.falha_ao_ler_chain': ['erro'],
  // Senha da sala. O servidor NUNCA diz qual era; só se passou.
  /** A sala tem senha e o pedido — entrar ou assistir — veio sem ela. */
  'lobby.senha_obrigatoria': [],
  'lobby.senha_incorreta': [],
  /** Tamanho fora de `min..max` (ao criar, ou uma tentativa absurda). */
  'lobby.senha_invalida': ['min', 'max'],
  /** `segundos` até poder tentar de novo nesta sala. */
  'lobby.senha_muitas_tentativas': ['segundos'],
  // Convite por código.
  'lobby.codigo_nao_encontrado': [],
  /** Não tem a forma de um código: seis caracteres do alfabeto sem 0/O/1/I. */
  'lobby.codigo_invalido': [],
  /** `segundos` até poder tentar outro código. */
  'lobby.codigo_muitas_tentativas': ['segundos'],

  // ------------------------------------------------------------------ perfil
  /** Tamanho ou caracteres fora do permitido (ver `apelido.ts`). */
  'perfil.apelido_invalido': ['min', 'max'],
  'perfil.apelido_proibido': [],
  /** `segundos` até poder trocar de novo. */
  'perfil.muitas_trocas': ['segundos'],

  // ----------------------------------------------------------------- partida
  'partida.nenhuma': [],
  /* Já está em outra partida: abrir uma segunda apagaria o vínculo da
     primeira, e as tacadas nela passariam a ser recusadas. Ver `Matches.open`. */
  'partida.ja_em_partida': [],
  'partida.ja_comprometido': [],
  'partida.quebra_ja_definida': [],
  'partida.ja_revelou': [],
  'partida.nonce_nao_confere': [],
  'partida.compromisso_diverge_da_chain': [],
  'partida.nao_espera_tacada': [],
  'partida.nao_e_sua_vez': [],
  'partida.coloque_a_branca': [],
  'partida.limite_gravavel': [],
  'partida.declare_a_cacapa': [],
  'partida.sem_bola_na_mao': [],
  'partida.bola_na_mao_nao_e_sua': [],
  'partida.posicao_invalida': [],
  /** Falta na quebra: a branca tem de ir atrás da linha da cabeça. */
  'partida.branca_atras_da_linha': [],
  'partida.sem_decisao_pendente': [],
  'partida.escolha_nao_e_sua': [],
  'partida.opcao_inexistente': ['opcao'],
  'partida.ainda_nao_comecou': [],
  'partida.carteira_fora_da_partida': [],
  'partida.angulo_fora_da_faixa': ['valor'],
  'partida.forca_fora_da_faixa': ['valor'],
  'partida.efeito_fora_da_faixa': ['eixo', 'valor'],
  /* Efeito dentro da faixa do byte, mas fora do disco da falha de taco: só um
     cliente modificado chega aqui. Ver `validarTacada`. */
  'partida.efeito_alem_do_limite': ['valor'],

  // -------------------------------------------------------------- espectador
  /** A sala foi criada sem plateia; quem criou decidiu assim. */
  'espectador.sala_nao_permite': [],
  /** A sala ainda espera oponente: não há partida para mostrar. */
  'espectador.partida_ainda_nao_comecou': [],
  /** Jogador da própria mesa pediu para assistir a ela. */
  'espectador.joga_nesta_mesa': [],

  // ---------------------------------------------------------------- conversa
  'chat.vazia': [],
  /** `maximo` é `CHAT_MAX_CHARS`. */
  'chat.longa_demais': ['maximo'],
  'chat.rapido_demais': [],
  'chat.fora_da_mesa': [],

  'voz.desligada': [],
  'voz.fora_da_mesa': [],
  'voz.indisponivel': [],

  // -------------------------------------------------------------- liquidação
  'liquidacao.sem_chave': [],
  'liquidacao.sem_revelacao': [],
  'liquidacao.sem_vencedor': [],
  'liquidacao.id_invalido': ['id'],
  'liquidacao.carteira_invalida': [],
  'liquidacao.ja_liquidada': [],
  'liquidacao.falha_na_chain': ['erro'],
  'liquidacao.inalcancavel': [],

  // ------------------------------------------------------------------ faucet
  'faucet.autentique': [],
  'faucet.so_devnet': [],
  'faucet.endereco_invalido': [],
  /** `segundos` até poder pedir de novo. */
  'faucet.aguarde': ['segundos'],
  'faucet.limite_da_rede': ['segundos'],
  'faucet.airdrop_no_limite': [],
  'faucet.sem_saldo': [],
  'faucet.falha_ao_enviar': ['erro'],

  // --------------------------------------------------------------- histórico
  /** O endereço pedido em `/api/history/:address` não é uma chave Solana. */
  'historico.endereco_invalido': [],
  /**
   * O servidor está sem banco (`DATABASE_URL` ausente), logo sem índice. O
   * cliente cai para a leitura direta da chain — o histórico não some, só
   * fica mais lento.
   */
  'historico.sem_indice': [],
  /** `segundos` até poder pedir outra reconciliação (`?refresh=1`) deste endereço. */
  'historico.aguarde': ['segundos'],

  // ------------------------------------------------------------- mint de tacos
  //
  // O domínio aqui é `mint`, não `tacos.mint`: o catálogo de eventos exige um
  // único ponto (`eventos.test.ts`), diferente das MENSAGENS do protocolo
  // (`tacos.mint`, `tacos.mint.estado`, `tacos.mint.ok`), que não têm essa
  // restrição. Decisão registrada na Tarefa 2 do plano do mint público.
  /** Esta carteira já cunhou o taco da coleção; só um por carteira. */
  'mint.ja_tem': [],
  /** O teto de cunhagem foi atingido; não sobrou taco para cunhar. */
  'mint.esgotado': [],
  /** O pagamento foi achado, mas é de outro pagador, para outro destino ou abaixo do preço. */
  'mint.pagamento_invalido': [],
  /**
   * O pagamento não apareceu na chain depois de algumas tentativas.
   *
   * Separado de `pagamento_invalido` porque o desfecho para o jogador é o
   * oposto: aqui ele deve ESPERAR e tentar de novo (a assinatura fica
   * guardada, não se paga duas vezes), lá não adianta insistir.
   */
  'mint.pagamento_nao_encontrado': [],
  /** Essa assinatura de pagamento já foi usada para cunhar antes. */
  'mint.pagamento_usado': [],
  /** O mint tem preço, mas o pedido não trouxe pagamento algum. */
  'mint.sem_pagamento': [],
  /** A cunhagem pública está desligada neste servidor. */
  'mint.indisponivel': [],
  /**
   * A chain falhou ao confirmar o pagamento ou a cunhagem.
   *
   * SEM parâmetro, e isso é a correção: o texto cru do erro da RPC ia inteiro
   * para o cliente, e ele costuma trazer a URL do endpoint — com o token de
   * acesso dentro, nos provedores pagos. O erro completo fica no log do
   * servidor, que é onde ele serve para alguma coisa.
   */
  'mint.rpc': [],
  /**
   * Cunhagens demais em pouco tempo — o ritmo por CARTEIRA cortou.
   *
   * Separado de `mint.indisponivel` de propósito: um manda esperar, o outro
   * manda desistir. Enquanto os dois eram o mesmo código, quem clicou rápido
   * lia "a cunhagem está desligada neste servidor" e ia embora.
   */
  'mint.rapido_demais': [],

  // ---------------------------------------------------------- upgrade de taco
  //
  // Domínio `upgrade`, um ponto só — a mesma regra do domínio `mint` acima; as
  // MENSAGENS são `tacos.upgrade` e `tacos.upgrade.ok`.
  //
  // Todos SEM parâmetro: quanto falta de orçamento, qual é o nível máximo e
  // onde está o teto já viajam no `upgrade` do próprio taco, que a tela tem em
  // mãos. Repetir o número na frase seria uma segunda fonte para a mesma
  // verdade.
  /** O taco pedido não é desta carteira. */
  'upgrade.nao_eh_seu': [],
  /** Taco gratuito não evolui: só NFT tem níveis. */
  'upgrade.gratuito': [],
  /** O orçamento de níveis desta raridade acabou. */
  'upgrade.orcamento': [],
  /** Este atributo já está no nível máximo. */
  'upgrade.nivel_maximo': [],
  /** Este atributo já está no teto de pontos — o nível não caberia inteiro. */
  'upgrade.teto_do_atributo': [],
  /** O pagamento foi achado, mas é de outro pagador, para outro destino ou abaixo do preço. */
  'upgrade.pagamento_invalido': [],
  /** Essa assinatura de pagamento já foi usada para subir um nível antes. */
  'upgrade.pagamento_usado': [],
  /** O pagamento não apareceu na chain ainda; esperar e tentar de novo (ver `mint.pagamento_nao_encontrado`). */
  'upgrade.pagamento_nao_encontrado': [],
  /** O nível tem preço, mas o pedido não trouxe pagamento algum. */
  'upgrade.sem_pagamento': [],
  /** O atributo pedido não existe no catálogo — cliente velho ou pedido forjado. */
  'upgrade.atributo_invalido': [],
  /** O upgrade está desligado neste servidor. */
  'upgrade.indisponivel': [],
  /**
   * A chain ou o banco falharam ao aplicar o nível.
   *
   * SEM parâmetro, pelo mesmo motivo de `mint.rpc`: o erro da RPC traz a URL
   * do endpoint (com o token, nos provedores pagos) e o do Drizzle traz a
   * query e os params. O erro inteiro fica no log do servidor.
   *
   * Distinto de `upgrade.indisponivel`: aqui o upgrade EXISTE e falhou agora,
   * então tentar de novo é a coisa certa a fazer — e, se o pagamento já tinha
   * sido conferido, ele está registrado para a devolução.
   */
  'upgrade.rpc': [],
  /** Upgrades demais em pouco tempo — o mesmo ritmo por carteira do mint. */
  'upgrade.rapido_demais': [],
} as const satisfies Record<string, readonly string[]>

export type EventoCode = keyof typeof EVENTOS

/** Os parâmetros que um código carrega, pelo nome. */
export type ParametrosDe<K extends EventoCode> = Record<
  (typeof EVENTOS)[K][number],
  string | number
>

/**
 * Um evento pronto para viajar.
 *
 * Código sem parâmetro vai sem `params`; código com parâmetro os EXIGE. É o
 * tipo que impede o servidor de mandar `partida.opcao_inexistente` sem dizer
 * qual opção — o cliente escreveria "{opcao}" na tela.
 */
export type Evento = {
  [K in EventoCode]: (typeof EVENTOS)[K] extends readonly []
    ? { code: K; params?: never }
    : { code: K; params: ParametrosDe<K> }
}[EventoCode]

/** Todos os códigos, para o schema e para os testes de paridade. */
export const EVENTO_CODES = Object.keys(EVENTOS) as [EventoCode, ...EventoCode[]]

/**
 * O evento na rede.
 *
 * `params` é um mapa solto de propósito: o schema valida que o código existe,
 * e é o CATÁLOGO acima — via `evento()` — que garante os parâmetros certos na
 * origem. Repetir cada assinatura aqui seria uma segunda lista para
 * desatualizar.
 */
export const EventoSchema = z.object({
  code: z.enum(EVENTO_CODES),
  params: z.record(z.union([z.string(), z.number()])).optional(),
})

/**
 * Monta um evento com os parâmetros conferidos pelo tipo.
 *
 *   evento('partida.nao_e_sua_vez')
 *   evento('partida.opcao_inexistente', { opcao: 7 })
 *
 * Passar parâmetro a mais, a menos, ou com nome errado não compila.
 */
export function evento<K extends EventoCode>(
  code: K,
  ...resto: (typeof EVENTOS)[K] extends readonly [] ? [] : [ParametrosDe<K>]
): Evento {
  const params = resto[0]
  return (params ? { code, params } : { code }) as Evento
}
